import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LoaderCircle, SearchCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormError } from "@/components/auth/AuthLayout";
import { FormField, inputClass } from "@/components/form/FormField";
import { PageHeading } from "@/components/PageHeading";
import { ComplaintSummary } from "@/components/progress/ComplaintSummary";
import { ProgressTimeline } from "@/components/progress/ProgressTimeline";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const TrackResult = ({ result, user }) => (
  <div className="fade-up space-y-6" data-testid="track-result">
    <ComplaintSummary complaint={result.complaint} attachmentsCount={result.complaint.attachments_count} />
    <section className="space-y-4">
      <h3 className="font-heading text-lg font-bold text-slate-900">Riwayat Progres Penyelesaian</h3>
      <ProgressTimeline updates={result.updates} />
      <p className="text-sm text-slate-500">
        Ingin membalas tanggapan pengurus RT?{" "}
        <Link to={user?.role === "warga" ? "/pengaduan-saya" : "/masuk"} className="font-semibold text-emerald-700 hover:underline" data-testid="track-reply-link">
          {user?.role === "warga" ? "Buka Pengaduan Saya" : "Masuk ke akun warga"}
        </Link>
      </p>
    </section>
  </div>
);

export default function TrackPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(params.get("tiket") || "");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!ticket.trim() || !phone.trim()) return setError("Masukkan nomor tiket dan nomor HP pelapor.");
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/track", { ticket: ticket.trim(), nomor_hp: phone.trim() });
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(formatApiError(err, "Pengaduan tidak ditemukan."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mx-auto max-w-4xl space-y-8 px-4 py-8 sm:px-6" data-testid="track-page">
      <PageHeading eyebrow="Lacak Tiket" title="Pantau Progres Pengaduan" description="Masukkan nomor tiket dan nomor HP yang digunakan saat mengirim pengaduan." testId="track-title" />
      <form onSubmit={submit} noValidate className="fade-up grid gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-[1fr_1fr_auto] sm:items-end sm:p-6" data-testid="track-form">
        <FormField id="track-ticket" label="Nomor Tiket">
          <Input id="track-ticket" value={ticket} onChange={(e) => setTicket(e.target.value.toUpperCase())} placeholder="#MRK-20261004-001" className={`${inputClass(false)} font-mono`} data-testid="track-ticket-input" />
        </FormField>
        <FormField id="track-phone" label="Nomor HP Pelapor">
          <Input id="track-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="081234567890" className={inputClass(false)} data-testid="track-phone-input" />
        </FormField>
        <Button type="submit" disabled={loading} className="h-11 rounded-xl bg-emerald-600 px-6 font-semibold hover:bg-emerald-700" data-testid="track-submit-btn">
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <SearchCheck className="h-4 w-4" />} Lacak
        </Button>
        <div className="sm:col-span-3"><FormError message={error} testId="track-error" /></div>
      </form>
      {result && <TrackResult result={result} user={user} />}
    </main>
  );
}
